/**
 * ═══════════════════════════════════════════════════════════════
 * CART GIFT SELECTION — v11 (fix false dismissals on self-removes)
 * ═══════════════════════════════════════════════════════════════
 *
 * Bugfix vs v10:
 *   When the customer removed the only main product, we auto-removed
 *   the gift (tier stopped qualifying). On the *next* cart update —
 *   when the customer re-added the main product — the diff between
 *   the stale snapshot (gift present) and the new cart (gift gone)
 *   looked like a customer-initiated removal, falsely recording a
 *   dismissal.
 *
 *   Two coordinated fixes:
 *
 *   1. Track gift _gift_threshold_v2 strings WE remove (in
 *      `selfRemovedKeys`). The dismissal detector ignores keys in
 *      this set — they're our doing, not the customer's.
 *
 *   2. After reconciliation, update `previousGiftKeysInCart` to
 *      reflect the *resulting* cart state (cart minus self-removes
 *      plus self-adds), so the next event diffs against reality.
 */

const CART_UPDATE_DEBOUNCE_MS = 50;
const DISMISSAL_STORAGE_KEY = 'cart_gift_dismissals_v1';

class GiftSelectionManager {
  constructor() {
    this.modal = document.getElementById('gift-selection-modal');
    this.modalContent = this.modal?.querySelector('[data-gift-modal-content]');
    this.modalTitle = this.modal?.querySelector('[data-gift-modal-title]');
    this.modalSubtitle = this.modal?.querySelector('[data-gift-modal-subtitle]');
    this.confirmBtn = this.modal?.querySelector('[data-gift-modal-confirm]');

    this.giftTiers = null;
    this.freebies = null;
    this.shownThresholds = new Set();
    // Tiers whose /cart/add.js failed this session (sold out, price change,
    // any 422). Reconciliation skips them so a failing tier doesn't retry on
    // every cart update; a page reload retries naturally.
    this.failedThresholds = new Set();

    // Modal state
    this.currentThreshold = null;
    this.selectedGift = null;
    this.currentProductVariants = null;
    this.currentProductOptions = null;
    this.productDataCache = null;

    // Async coordination
    this.processingChain = Promise.resolve();
    this.reconciliationInFlight = null;
    this.debounceTimer = null;
    this.latestCart = null;
    this.pendingCartFromEvent = null;
    this.selfDispatched = false;

    // Dismissal tracking
    this.dismissedKeys = this.loadDismissals();
    // Snapshot of gift line _gift_threshold_v2 strings from previous evaluation,
    // used to detect customer-initiated removals between cart updates.
    this.previousGiftKeysInCart = null;
    // Gift _gift_threshold_v2 strings WE removed (because tier stopped qualifying,
    // or we're swapping the selection). These must NOT be counted as dismissals
    // when they show up "missing" in the next cart diff.
    this.selfRemovedKeys = new Set();

    this.init();
  }

  // ─────────────────────────────────────────────────────────────
  // Initialization
  // ─────────────────────────────────────────────────────────────

  init() {
    if (!this.modal) {
      console.warn('[GiftSelection] Modal element not found');
      return;
    }

    this.bindEvents();
    this.loadGiftTiers();
    this.loadFreebies();

    document.addEventListener('cart:updated', (e) => {
      if (e.detail?.cart) this.pendingCartFromEvent = e.detail.cart;
      this.scheduleCartCheck();
    });

    this.scheduleCartCheck();
  }

  bindEvents() {
    this.modal.querySelectorAll('[data-gift-modal-close]').forEach((el) => {
      el.addEventListener('click', () => this.closeModal());
    });

    this.confirmBtn?.addEventListener('click', () => this.confirmSelection());

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.modal.getAttribute('aria-hidden') === 'false') {
        this.closeModal();
      }
    });

    document.addEventListener('click', (e) => {
      const changeLink = e.target.closest('[data-change-gift]');
      if (changeLink) {
        e.preventDefault();
        const threshold = parseInt(changeLink.dataset.changeGift);
        this.openModalForThreshold(threshold, true);
      }
    });
  }

  loadGiftTiers() {
    const milestonesBar = document.querySelector('.cart-milestones');
    if (!milestonesBar) return;

    let rawTiers = [];
    try {
      rawTiers = JSON.parse(milestonesBar.dataset.giftTiers || '[]');
    } catch (err) {
      console.error('[GiftSelection] Invalid gift-tiers JSON:', err);
      rawTiers = [];
    }

    this.giftTiers = {};
    rawTiers.forEach((tier) => {
      if (!tier?.threshold) return;
      const products = tier.products || [];
      if (products.length === 0) return;

      this.giftTiers[tier.threshold] = this.buildTierData(products, tier.addAllGifts === true);
    });
  }

  loadFreebies() {
    const milestonesBar = document.querySelector('.cart-milestones');
    if (!milestonesBar) return;

    let rawFreebies = [];
    try {
      rawFreebies = JSON.parse(milestonesBar.dataset.freebies || '[]');
    } catch (err) {
      console.error('[GiftSelection] Invalid freebies JSON:', err);
      rawFreebies = [];
    }

    this.freebies = {};
    rawFreebies.forEach((entry) => {
      if (!entry?.key) return;
      const products = entry.products || [];
      if (products.length === 0) return;

      this.freebies[entry.key] = {
        key: entry.key,
        ...this.buildTierData(products, entry.addAllGifts === true),
      };
    });
  }

  buildTierData(products, addAllGifts) {
    // Only products with at least one available variant are offerable, and
    // auto-add must target an available variant — the Liquid's firstVariantId
    // is the blind first variant, which /cart/add.js rejects when sold out.
    // A tier with nothing available is soldOut: the Liquid hides its
    // milestone, and reconcileTier skips it (belt for stale pages).
    const availableProducts = (products || [])
      .map((product) => {
        const firstAvailable = (product.variants || []).find((v) => v.available);
        if (!firstAvailable) return null;
        return {
          ...product,
          firstAvailableVariantId: firstAvailable.id,
          // Gift value = compare-at first, matching the Liquid's product
          // price and the modal paths. Gift SKUs are priced 0 with the real
          // value in compare_at_price — raw variant price would be 0.00.
          firstAvailablePrice:
            firstAvailable.compare_at_price || firstAvailable.price || 0,
        };
      })
      .filter(Boolean);
    const soldOut = availableProducts.length === 0;

    if (addAllGifts) {
      return {
        products: availableProducts,
        addAllGifts: true,
        needsModal: false,
        soldOut,
      };
    }

    const first = availableProducts[0] || null;
    const availableVariantCount = first
      ? first.variants.filter((v) => v.available).length
      : 0;
    // Modal only when there is a real choice among what's in stock; a
    // product whose other variants are sold out auto-adds its last one.
    const needsModal = availableProducts.length > 1 || availableVariantCount > 1;

    return {
      products: availableProducts,
      addAllGifts: false,
      needsModal,
      soldOut,
      autoAddVariantId: !needsModal && first ? first.firstAvailableVariantId : null,
      autoAddHandle: !needsModal && first ? first.handle : null,
      autoAddPrice: !needsModal && first ? first.firstAvailablePrice : null,
    };
  }

  // ─────────────────────────────────────────────────────────────
  // Dismissal tracking
  // ─────────────────────────────────────────────────────────────

  loadDismissals() {
    try {
      const raw = localStorage.getItem(DISMISSAL_STORAGE_KEY);
      if (!raw) return new Set();
      const parsed = JSON.parse(raw);
      return new Set(Array.isArray(parsed) ? parsed : []);
    } catch (err) {
      console.warn('[GiftSelection] Could not load dismissals:', err);
      return new Set();
    }
  }

  saveDismissals() {
    try {
      localStorage.setItem(
        DISMISSAL_STORAGE_KEY,
        JSON.stringify([...this.dismissedKeys])
      );
    } catch (err) {
      console.warn('[GiftSelection] Could not save dismissals:', err);
    }
  }

  /**
   * Detect line items that disappeared since last evaluation.
   * If a tier still qualifies but its line(s) are gone, the customer
   * removed them — record as dismissed.
   *
   * IMPORTANT: keys we removed ourselves (selfRemovedKeys) are
   * filtered out — those vanished because of our reconciliation,
   * not customer intent.
   */
  detectAndRecordDismissals(cart, qualifiesByThreshold) {
    if (this.previousGiftKeysInCart === null) {
      // First evaluation — nothing to diff against
      return;
    }

    const currentGiftKeys = new Set(
      cart.items
        .filter((item) => item.properties?._free_gift_v2 === 'true')
        .map((item) => item.properties?._gift_threshold_v2)
        .filter(Boolean)
    );

    let changed = false;
    for (const previousKey of this.previousGiftKeysInCart) {
      if (currentGiftKeys.has(previousKey)) continue; // still there

      // Skip keys WE removed — not a customer dismissal.
      if (this.selfRemovedKeys.has(previousKey)) continue;

      // It disappeared. Was the tier still qualifying?
      const baseThreshold = this.extractBaseThreshold(previousKey);
      if (qualifiesByThreshold.get(baseThreshold) === true) {
        if (!this.dismissedKeys.has(previousKey)) {
          this.dismissedKeys.add(previousKey);
          changed = true;
          console.log(`[GiftSelection] Recorded dismissal: ${previousKey}`);
        }
      }
    }

    if (changed) this.saveDismissals();
  }

  /**
   * From a _gift_threshold_v2 value like "10000:53055..." or "FREEBIE:abc:53055...",
   * return the base threshold key ("10000" or "FREEBIE:abc") used for tier lookup.
   */
  extractBaseThreshold(giftThresholdValue) {
    if (giftThresholdValue.startsWith('FREEBIE:')) {
      // FREEBIE:abc or FREEBIE:abc:VAR
      const parts = giftThresholdValue.split(':');
      // parts[0] = 'FREEBIE', parts[1] = freebie key, parts[2] = variant (optional)
      return `FREEBIE:${parts[1]}`;
    }
    // "10000" or "10000:VAR"
    return giftThresholdValue.split(':')[0];
  }

  /**
   * Clear dismissals for a specific tier (when it stops qualifying).
   * Removes both the bare threshold and any composite ("{threshold}:VAR") variants.
   */
  clearDismissalsForTier(threshold) {
    const prefix = `${threshold}:`;
    let changed = false;
    for (const key of [...this.dismissedKeys]) {
      if (key === threshold || key.startsWith(prefix)) {
        this.dismissedKeys.delete(key);
        changed = true;
      }
    }
    if (changed) this.saveDismissals();
  }

  /**
   * Compute the dismissal key for a candidate add.
   * Mirrors buildLineThreshold so dismissals match the line items
   * they would have been added as.
   */
  dismissalKeyFor(threshold, variantId, isAddAll) {
    return isAddAll ? `${threshold}:${variantId}` : String(threshold);
  }

  // ─────────────────────────────────────────────────────────────
  // Cart event handling — debounced & idle-deferred
  // ─────────────────────────────────────────────────────────────

  scheduleCartCheck() {
    if (this.selfDispatched) {
      this.selfDispatched = false;
      return;
    }

    if (this.debounceTimer) clearTimeout(this.debounceTimer);
    this.debounceTimer = setTimeout(() => {
      this.debounceTimer = null;
      this.runWhenIdle(() => this.enqueue(() => this.onCartUpdated()));
    }, CART_UPDATE_DEBOUNCE_MS);
  }

  flushPendingCheck() {
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
      this.debounceTimer = null;
    }
  }

  reconcileNow() {
    this.flushPendingCheck();
    if (this.reconciliationInFlight) {
      return this.reconciliationInFlight.then(() => this.reconcileNow());
    }
    const run = this.enqueue(() => this.onCartUpdated());
    this.reconciliationInFlight = run.finally(() => {
      this.reconciliationInFlight = null;
    });
    return this.reconciliationInFlight;
  }

  runWhenIdle(fn) {
    if (typeof requestIdleCallback === 'function') {
      requestIdleCallback(fn, { timeout: 200 });
    } else {
      setTimeout(fn, 0);
    }
  }

  enqueue(task) {
    this.processingChain = this.processingChain.then(task).catch((err) => {
      console.error('[GiftSelection] Queued task failed:', err);
    });
    return this.processingChain;
  }

  async getCurrentCart() {
    if (this.pendingCartFromEvent) {
      const cart = this.pendingCartFromEvent;
      this.pendingCartFromEvent = null;
      this.latestCart = cart;
      return cart;
    }
    return this.fetchCart();
  }

  async fetchCart() {
    const response = await fetch('/cart.js');
    const cart = await response.json();
    this.latestCart = cart;
    return cart;
  }

  async onCartUpdated() {
    if (!this.giftTiers && !this.freebies) return this.fetchCart();

    const cart = await this.getCurrentCart();
    const nonGiftTotal = this.calculateNonGiftTotal(cart);
    const nonGiftItemCount = this.countNonGiftItems(cart);

    // Build qualifies-by-threshold map up front so dismissal detection knows
    // whether each base threshold was qualifying when its line disappeared.
    const qualifiesByThreshold = new Map();
    if (this.giftTiers) {
      for (const threshold of Object.keys(this.giftTiers)) {
        qualifiesByThreshold.set(String(threshold), nonGiftTotal >= parseInt(threshold));
      }
    }
    if (this.freebies) {
      for (const freebieKey of Object.keys(this.freebies)) {
        qualifiesByThreshold.set(`FREEBIE:${freebieKey}`, nonGiftItemCount > 0);
      }
    }

    // Detect customer-initiated removals BEFORE reconciliation.
    // Anything WE remove in the same pass below will go into selfRemovedKeys
    // first so it's excluded from the diff next time around.
    this.detectAndRecordDismissals(cart, qualifiesByThreshold);

    const giftsToRemove = [];
    const giftsToAutoAdd = [];
    const modalsToOpen = [];

    if (this.giftTiers) {
      for (const [threshold, tierData] of Object.entries(this.giftTiers)) {
        const thresholdNum = parseInt(threshold);
        const linesForTier = this.findLinesForThreshold(cart, String(thresholdNum));
        const qualifies = qualifiesByThreshold.get(String(thresholdNum));

        this.reconcileTier({
          threshold: String(thresholdNum),
          tierData,
          qualifies,
          linesInCart: linesForTier,
          giftsToRemove,
          giftsToAutoAdd,
          modalsToOpen,
        });
      }
    }

    if (this.freebies) {
      for (const [freebieKey, freebieData] of Object.entries(this.freebies)) {
        const freebieThreshold = `FREEBIE:${freebieKey}`;
        const linesForFreebie = this.findLinesForThreshold(cart, freebieThreshold);
        const qualifies = qualifiesByThreshold.get(freebieThreshold);

        this.reconcileTier({
          threshold: freebieThreshold,
          tierData: freebieData,
          qualifies,
          linesInCart: linesForFreebie,
          giftsToRemove,
          giftsToAutoAdd,
          modalsToOpen,
        });
      }
    }

    // Mark every gift line we're about to remove as "self-removed" so the
    // next dismissal-detection pass ignores it. Use the line's actual
    // _gift_threshold_v2 property — same string we'd diff against later.
    giftsToRemove.forEach(({ lineKey }) => {
      const line = cart.items.find((item) => item.key === lineKey);
      const giftThresholdValue = line?.properties?._gift_threshold_v2;
      if (giftThresholdValue) this.selfRemovedKeys.add(giftThresholdValue);
    });

    if (giftsToRemove.length > 0) {
      await this.batchRemoveGifts(giftsToRemove);
    }
    let addSucceeded = true;
    if (giftsToAutoAdd.length > 0) {
      addSucceeded = await this.batchAddGifts(giftsToAutoAdd);
    }
    modalsToOpen.forEach((threshold) => {
      setTimeout(() => this.openModalForThreshold(threshold), 300);
    });

    // Snapshot what the cart actually looks like AFTER our reconciliation:
    //   start from the cart we just read,
    //   subtract gift lines we just removed,
    //   add gift threshold strings we just added.
    // This way the next event's diff compares against reality, not a stale
    // pre-reconciliation snapshot.
    const removedLineKeys = new Set(giftsToRemove.map((g) => g.lineKey));
    const postReconciliationGiftKeys = new Set(
      cart.items
        .filter((item) => item.properties?._free_gift_v2 === 'true')
        .filter((item) => !removedLineKeys.has(item.key))
        .map((item) => item.properties?._gift_threshold_v2)
        .filter(Boolean)
    );
    // Only count the added keys if the add actually landed — a failed add
    // recorded here would show up as "missing" in the next diff and be
    // misread as a customer dismissal, silently suppressing the tier.
    if (addSucceeded) {
      giftsToAutoAdd.forEach(({ threshold, tierData }) => {
        const variantId = tierData.autoAddVariantId;
        const isAddAll = this.isAddAllThreshold(threshold);
        postReconciliationGiftKeys.add(this.buildLineThreshold(threshold, variantId, isAddAll));
      });
    }
    this.previousGiftKeysInCart = postReconciliationGiftKeys;

    // selfRemovedKeys served its purpose for this pass. Clear it so a
    // future genuine customer removal of the same threshold (e.g. after
    // re-qualifying) isn't accidentally suppressed.
    this.selfRemovedKeys.clear();

    // Return the post-mutation cart so callers (e.g. checkout-guard) can
    // act on the reconciled state rather than the pre-mutation snapshot.
    return this.fetchCart();
  }

  reconcileTier({ threshold, tierData, qualifies, linesInCart, giftsToRemove, giftsToAutoAdd, modalsToOpen }) {
    if (qualifies) {
      // Sold-out tiers are hidden by the Liquid on the next render; a page
      // that pre-dates the stockout still has them, so skip quietly here.
      // Same for tiers whose add already failed this session — a stale page
      // would otherwise retry on every cart update.
      if (tierData.soldOut || this.failedThresholds.has(String(threshold))) return;

      if (tierData.addAllGifts) {
        // Bundle: add only products that are (a) not already in cart AND (b) not dismissed
        const presentVariantIds = new Set(linesInCart.map((line) => line.variant_id));
        tierData.products.forEach((product) => {
          const variantId = product.firstAvailableVariantId || product.firstVariantId;
          if (!variantId) return;
          if (presentVariantIds.has(variantId)) return;

          const dismissalKey = this.dismissalKeyFor(threshold, variantId, true);
          if (this.dismissedKeys.has(dismissalKey)) return;

          giftsToAutoAdd.push({
            threshold,
            tierData: {
              autoAddVariantId: variantId,
              autoAddHandle: product.handle,
              autoAddPrice: product.firstAvailablePrice ?? product.price,
            },
          });
        });
      } else if (tierData.needsModal) {
        if (linesInCart.length === 0 && !this.shownThresholds.has(threshold)) {
          // Don't open modal if customer dismissed this tier
          if (this.dismissedKeys.has(String(threshold))) return;

          this.shownThresholds.add(threshold);
          modalsToOpen.push(threshold);
        }
      } else {
        // Single auto-add
        if (linesInCart.length === 0) {
          if (this.dismissedKeys.has(String(threshold))) return;
          giftsToAutoAdd.push({ threshold, tierData });
        }
      }
    } else {
      // Doesn't qualify any more — remove every line item AND clear dismissals
      linesInCart.forEach((line) => {
        giftsToRemove.push({ threshold, lineKey: line.key });
      });
      this.shownThresholds.delete(threshold);
      this.clearDismissalsForTier(threshold);
    }
  }

  // ─────────────────────────────────────────────────────────────
  // Cart helpers
  // ─────────────────────────────────────────────────────────────

  calculateNonGiftTotal(cart) {
    let total = 0;
    cart.items.forEach((item) => {
      if (!item.properties?._free_gift_v2) total += item.final_line_price;
    });
    return total;
  }

  countNonGiftItems(cart) {
    return cart.items.filter((item) => !item.properties?._free_gift_v2).length;
  }

  findLinesForThreshold(cart, threshold) {
    const prefix = `${threshold}:`;
    return cart.items.filter((item) => {
      if (item.properties?._free_gift_v2 !== 'true') return false;
      const lineThreshold = item.properties?._gift_threshold_v2 || '';
      return lineThreshold === threshold || lineThreshold.startsWith(prefix);
    });
  }

  findGiftInCart(cart, threshold) {
    return cart.items.find(
      (item) =>
        item.properties?._free_gift_v2 === 'true' &&
        parseInt(item.properties?._gift_threshold_v2) === threshold
    );
  }

  // ─────────────────────────────────────────────────────────────
  // Modal — opening, closing, rendering
  // ─────────────────────────────────────────────────────────────

  resolveTier(threshold) {
    if (typeof threshold === 'string' && threshold.startsWith('FREEBIE:')) {
      const freebieKey = threshold.split(':')[1];
      return this.freebies?.[freebieKey];
    }
    return this.giftTiers?.[threshold];
  }

  async openModalForThreshold(threshold, isChange = false) {
    const tierData = this.resolveTier(threshold);
    if (!tierData || tierData.products.length === 0) return;
    if (tierData.addAllGifts) return;

    this.currentThreshold = threshold;
    this.selectedGift = null;
    this.currentProductVariants = null;

    this.modalTitle.textContent = isChange ? 'Change your gift' : 'Wähle dein Geschenk!';

    this.modalContent.innerHTML = '<div class="gift-modal__loading">Loading…</div>';
    this.confirmBtn.disabled = true;
    this.confirmBtn.textContent = isChange ? 'Change gift' : 'Geschenk Hinzufügen';
    this.modal.setAttribute('aria-hidden', 'false');
    document.body.style.overflow = 'hidden';

    const productData = this.buildProductData(tierData.products);

    if (this.modal.getAttribute('aria-hidden') === 'true') return;

    if (productData.length === 1 && productData[0].productData.variants.length > 1) {
      this.renderVariantSelector(productData[0], threshold);
    } else {
      this.renderProductOptions(productData, threshold);
    }
  }

  closeModal() {
    this.modal.setAttribute('aria-hidden', 'true');
    document.body.style.overflow = '';
  }

  buildProductData(products) {
    return products
      .filter((p) => p.variants && p.variants.length > 0)
      .map((product) => ({
        ...product,
        productData: {
          title: product.title,
          handle: product.handle,
          featured_image: product.image,
          images: product.image ? [product.image] : [],
          options: (product.options || []).map((name) => ({ name })),
          variants: product.variants,
        },
      }));
  }

  renderProductOptions(productData, threshold) {
    this.productDataCache = productData;
    let html = '<div class="gift-options">';

    productData.forEach((item, index) => {
      const product = item.productData;
      const variant = product.variants[0];
      const image = product.featured_image || product.images?.[0] || '';
      const price = variant.compare_at_price || variant.price || 0;
      const hasVariants = product.variants.length > 1;

      html += `
        <div class="gift-option-wrapper" data-product-index="${index}">
          <label class="gift-option"
            data-variant-id="${variant.id}"
            data-product-handle="${item.handle}"
            data-product-index="${index}"
            data-gift-threshold="${threshold}"
            data-gift-price="${price}"
            data-has-variants="${hasVariants}"
          >
            <input type="radio" name="gift-selection" class="gift-option__radio" value="${variant.id}">
            <div class="gift-option__check">
              <svg class="gift-option__check-icon" width="14" height="14" viewBox="0 0 14 14" fill="none">
                <path d="M11.5 4L5.5 10L2.5 7" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
            </div>
            <img class="gift-option__image" src="${image}" alt="${product.title}" loading="lazy">
            <div class="gift-option__details">
              <h3 class="gift-option__title">${product.title}</h3>
              ${hasVariants ? `<p class="gift-option__variant-hint">Multiple options available</p>` : ''}
              <div class="gift-option__price">
                <span class="gift-option__price-original">${this.formatMoney(price)}</span>
                <span class="gift-option__price-free">Kostenlos</span>
              </div>
            </div>
          </label>
          <div class="gift-inline-variants" style="display: none;"></div>
        </div>
      `;
    });

    html += '</div>';
    this.modalContent.innerHTML = html;
    this.bindProductOptionEvents(threshold);
  }

  bindProductOptionEvents(threshold) {
    this.modalContent.querySelectorAll('.gift-option').forEach((option) => {
      option.addEventListener('click', () => {
        this.modalContent.querySelectorAll('.gift-option').forEach((o) => o.classList.remove('is-selected'));
        this.modalContent.querySelectorAll('.gift-inline-variants').forEach((v) => {
          v.style.display = 'none';
          v.innerHTML = '';
        });

        option.classList.add('is-selected');
        option.querySelector('input')?.setAttribute('checked', 'checked');

        const hasVariants = option.dataset.hasVariants === 'true';

        if (hasVariants) {
          const productIndex = parseInt(option.dataset.productIndex);
          const wrapper = option.closest('.gift-option-wrapper');
          const variantPicker = wrapper.querySelector('.gift-inline-variants');
          this.renderInlineVariantPicker(this.productDataCache[productIndex], threshold, variantPicker);
          variantPicker.style.display = 'block';
          this.confirmBtn.disabled = true;
        } else {
          this.selectedGift = {
            variantId: parseInt(option.dataset.variantId),
            handle: option.dataset.productHandle,
            threshold: option.dataset.giftThreshold,
            price: parseInt(option.dataset.giftPrice),
          };
          this.confirmBtn.disabled = false;
        }
      });
    });
  }

  renderInlineVariantPicker(item, threshold, container) {
    const product = item.productData;
    const price = product.variants[0].compare_at_price || product.variants[0].price || 0;

    container.innerHTML = `
      <div class="gift-variants">
        ${this.renderVariantOptionGroups(product, false)}
        <input type="hidden" id="selected-variant-id" value="">
        <input type="hidden" id="selected-product-handle" value="${item.handle}">
        <input type="hidden" id="selected-gift-threshold" value="${threshold}">
        <input type="hidden" id="selected-gift-price" value="${price}">
      </div>
    `;

    this.currentProductVariants = product.variants;
    this.currentProductOptions = product.options;
    this.selectedGift = null;

    container.querySelectorAll('.gift-variant-btn').forEach((btn) => {
      btn.addEventListener('click', () => {
        const group = btn.closest('.gift-variants__group');
        group.querySelectorAll('.gift-variant-btn').forEach((b) => b.classList.remove('is-selected'));
        btn.classList.add('is-selected');
        this.updateInlineSelectedVariant(container);
      });
    });
  }

  renderVariantSelector(item, threshold) {
    const product = item.productData;
    const image = product.featured_image || product.images?.[0] || '';
    const price = product.variants[0].compare_at_price || product.variants[0].price || 0;

    this.modalContent.innerHTML = `
      <div class="gift-single-product">
        <div class="gift-option is-single" style="cursor: default; border-color: var(--color-primary, #000);">
          <img class="gift-option__image" src="${image}" alt="${product.title}" loading="lazy">
          <div class="gift-option__details">
            <h3 class="gift-option__title">${product.title}</h3>
            <div class="gift-option__price">
              <span class="gift-option__price-original">${this.formatMoney(price)}</span>
              <span class="gift-option__price-free">FREE</span>
            </div>
          </div>
        </div>
        <div class="gift-variants">
          ${this.renderVariantOptionGroups(product, true)}
          <input type="hidden" id="selected-variant-id" value="${product.variants[0].id}">
          <input type="hidden" id="selected-product-handle" value="${item.handle}">
          <input type="hidden" id="selected-gift-threshold" value="${threshold}">
          <input type="hidden" id="selected-gift-price" value="${price}">
        </div>
      </div>
    `;

    this.currentProductVariants = product.variants;
    this.currentProductOptions = product.options;

    this.selectedGift = {
      variantId: product.variants[0].id,
      handle: item.handle,
      threshold,
      price,
    };
    this.confirmBtn.disabled = false;

    this.bindOptionEvents();
  }

  renderVariantOptionGroups(product, preselectFirst) {
    return product.options
      .map((optionName, optionIndex) => {
        const optionValues = [...new Set(product.variants.map((v) => v.options[optionIndex]))];
        const buttons = optionValues
          .map((value, valueIndex) => {
            const selectedClass = preselectFirst && valueIndex === 0 ? ' is-selected' : '';
            return `<button type="button" class="gift-variant-btn${selectedClass}"
              data-option-index="${optionIndex}" data-option-value="${value}">${value}</button>`;
          })
          .join('');

        return `
          <div class="gift-variants__group" data-option-index="${optionIndex}">
            <span class="gift-variants__label">${optionName.name}:</span>
            <div class="gift-variants__options">${buttons}</div>
          </div>
        `;
      })
      .join('');
  }

  bindOptionEvents() {
    this.modalContent.querySelectorAll('.gift-variant-btn').forEach((btn) => {
      btn.addEventListener('click', () => {
        const group = btn.closest('.gift-variants__group');
        group.querySelectorAll('.gift-variant-btn').forEach((b) => b.classList.remove('is-selected'));
        btn.classList.add('is-selected');
        this.updateSelectedVariant();
      });
    });
  }

  // ─────────────────────────────────────────────────────────────
  // Variant selection
  // ─────────────────────────────────────────────────────────────

  updateInlineSelectedVariant(container) {
    if (!this.currentProductVariants) return;

    const selectedOptions = [];
    let allSelected = true;

    container.querySelectorAll('.gift-variants__group').forEach((group) => {
      const selected = group.querySelector('.gift-variant-btn.is-selected');
      if (selected) selectedOptions.push(selected.dataset.optionValue);
      else allSelected = false;
    });

    if (!allSelected) {
      this.confirmBtn.disabled = true;
      return;
    }

    const matchingVariant = this.findVariantByOptions(selectedOptions);

    if (matchingVariant && matchingVariant.available) {
      container.querySelector('#selected-variant-id').value = matchingVariant.id;
      this.selectedGift = {
        variantId: matchingVariant.id,
        handle: container.querySelector('#selected-product-handle').value,
        threshold: container.querySelector('#selected-gift-threshold').value,
        price: parseInt(container.querySelector('#selected-gift-price').value),
      };
      this.confirmBtn.disabled = false;
    } else {
      this.confirmBtn.disabled = true;
    }

    this.updateVariantAvailability(container);
  }

  updateSelectedVariant() {
    if (!this.currentProductVariants) return;

    const selectedOptions = [];
    this.modalContent.querySelectorAll('.gift-variants__group').forEach((group) => {
      const selected = group.querySelector('.gift-variant-btn.is-selected');
      if (selected) selectedOptions.push(selected.dataset.optionValue);
    });

    const matchingVariant = this.findVariantByOptions(selectedOptions);
    if (!matchingVariant) return;

    document.getElementById('selected-variant-id').value = matchingVariant.id;
    this.selectedGift = {
      variantId: matchingVariant.id,
      handle: document.getElementById('selected-product-handle').value,
      threshold: document.getElementById('selected-gift-threshold').value,
      price: parseInt(document.getElementById('selected-gift-price').value),
    };
    this.confirmBtn.disabled = !matchingVariant.available;

    this.updateVariantAvailability(this.modalContent);
  }

  findVariantByOptions(selectedOptions) {
    return this.currentProductVariants.find((v) =>
      selectedOptions.every((opt, idx) => v.options[idx] === opt)
    );
  }

  updateVariantAvailability(container) {
    container.querySelectorAll('.gift-variant-btn').forEach((btn) => {
      const optIdx = parseInt(btn.dataset.optionIndex);
      const optVal = btn.dataset.optionValue;
      const hasAvailable = this.currentProductVariants.some(
        (v) => v.options[optIdx] === optVal && v.available
      );
      btn.disabled = !hasAvailable;
    });
  }

  // ─────────────────────────────────────────────────────────────
  // Cart mutations
  // ─────────────────────────────────────────────────────────────

  confirmSelection() {
    if (!this.selectedGift) return;

    const gift = this.selectedGift;
    this.confirmBtn.disabled = true;
    this.confirmBtn.textContent = 'Adding...';

    this.closeModal();

    // Customer chose a gift via the modal — clear any prior dismissal for this tier
    this.clearDismissalsForTier(String(gift.threshold));

    this.enqueue(async () => {
      try {
        const cart = await this.getCurrentCart();
        const existing = this.findGiftInCart(cart, gift.threshold);
        if (existing) {
          // Mark the swap-out as self-initiated so the diff doesn't see it as a dismissal
          const oldKey = existing.properties?._gift_threshold_v2;
          if (oldKey) this.selfRemovedKeys.add(oldKey);
          await this.removeGiftByLineKey(existing.key, gift.threshold);
        }
        await this.batchAddGifts([
          {
            threshold: gift.threshold,
            tierData: {
              autoAddVariantId: gift.variantId,
              autoAddHandle: gift.handle,
              autoAddPrice: gift.price,
            },
          },
        ]);
      } catch (err) {
        console.error('[GiftSelection] Confirm error:', err);
      }
    });
  }

  getBundledSections() {
    const sectionsToBundle = [];
    document.documentElement.dispatchEvent(
      new CustomEvent('cart:bundled-sections', {
        bubbles: true,
        detail: { sections: sectionsToBundle },
      })
    );
    return sectionsToBundle;
  }

  buildLineThreshold(threshold, variantId, isAddAll) {
    return isAddAll ? `${threshold}:${variantId}` : String(threshold);
  }

  async batchAddGifts(gifts) {
    if (gifts.length === 0) return true;

    const sections = this.getBundledSections();
    const items = gifts.map(({ threshold, tierData }) => {
      const variantId = tierData.autoAddVariantId;
      const isAddAll = this.isAddAllThreshold(threshold);
      return {
        id: variantId,
        quantity: 1,
        properties: {
          _free_gift_v2: 'true',
          _gift_threshold_v2: this.buildLineThreshold(threshold, variantId, isAddAll),
          _gift_value: ((tierData.autoAddPrice || 0) / 100).toFixed(2),
        },
      };
    });

    let response;
    try {
      response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items, sections }),
      });
    } catch (err) {
      this.rememberFailedGiftAdd(gifts, `network: ${err?.message}`);
      return false;
    }

    if (!response.ok) {
      let detail = null;
      try {
        detail = await response.json();
      } catch (_) {
        // body unreadable — status alone is enough for the log
      }
      const reason = detail?.description || detail?.message || `status ${response.status}`;
      this.rememberFailedGiftAdd(gifts, reason);
      return false;
    }

    const data = await response.json();
    this.publishCartUpdate(this.buildPublishPayload(data));

    const summary = gifts.map((g) => `${g.threshold} (${g.tierData.autoAddHandle})`).join(', ');
    console.log(`[GiftSelection] Added ${gifts.length} gift(s): ${summary}`);
    return true;
  }

  // /cart/add.js with an items array is all-or-nothing: one sold-out gift
  // fails the whole batch, so every threshold in it is suspended for the
  // session. A reload retries naturally — by then stock may be back, or the
  // bar re-rendered without the sold-out tier.
  rememberFailedGiftAdd(gifts, reason) {
    gifts.forEach((g) => this.failedThresholds.add(String(g.threshold)));
    const summary = gifts.map((g) => g.threshold).join(', ');
    console.error(
      `[GiftSelection] Gift add failed (${reason}); suspending tier(s) for this session: ${summary}`
    );
  }

  isAddAllThreshold(threshold) {
    const tier = this.resolveTier(threshold);
    return tier?.addAllGifts === true;
  }

  async removeGiftByLineKey(lineKey, threshold) {
    const sections = this.getBundledSections();

    const response = await fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: lineKey, quantity: 0, sections }),
    });

    const data = await response.json();
    this.latestCart = data;
    this.publishCartUpdate(data);
    console.log(`[GiftSelection] Gift removed for threshold: ${threshold}`);
  }

  async batchRemoveGifts(gifts) {
    if (gifts.length === 0) return;
    if (gifts.length === 1) {
      return this.removeGiftByLineKey(gifts[0].lineKey, gifts[0].threshold);
    }

    const sections = this.getBundledSections();
    const updates = {};
    gifts.forEach((g) => {
      updates[g.lineKey] = 0;
    });

    const response = await fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ updates, sections }),
    });

    const data = await response.json();
    this.latestCart = data;
    this.publishCartUpdate(data);
    console.log(
      `[GiftSelection] Batch-removed ${gifts.length} gifts for thresholds: ${gifts.map((g) => g.threshold).join(', ')}`
    );
  }

  buildPublishPayload(mutationResponse) {
    const base = this.latestCart || {};
    return {
      ...base,
      items: base.items || [],
      sections: mutationResponse.sections || {},
    };
  }

  publishCartUpdate(cart) {
    this.selfDispatched = true;

    if (window.theme?.pubsub?.publish) {
      theme.pubsub.publish(theme.pubsub.PUB_SUB_EVENTS.cartUpdate, {
        source: 'gift-selection',
        cart,
      });
    } else {
      this.selfDispatched = false;
      document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart } }));
    }
  }

  // ─────────────────────────────────────────────────────────────
  // Utilities
  // ─────────────────────────────────────────────────────────────

  formatMoney(cents) {
    try {
      if (window.theme?.Currency?.formatMoney && window.Shopify?.currency?.active) {
        return window.theme.Currency.formatMoney(cents);
      }
    } catch (e) {
      // fallback below
    }
    const currency = window.Shopify?.currency?.active || 'EUR';
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency,
    }).format(cents / 100);
  }
}

const bootGiftSelection = () => {
  window.__giftSelection = new GiftSelectionManager();
};
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', bootGiftSelection);
} else {
  bootGiftSelection();
}